import { useBillingServiceStore } from '@billing/AppProvider'
import { ServiceOrderReportDto } from '@billing/app/api'
import ReportStep from '@billing/app/models/report-step.enum'
import { Box, CircularProgress, Stack } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  EmptyIndicator,
  TomTable,
  tomTableColumnFilterPresets,
  useDefaultMRTOptions,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import {
  exportToCsv,
  IDataExportConfig,
} from 'modules/ui/src/theme/components/table/utils/xlsxUtils'
import { useEffect, useMemo } from 'react'
import { CustomerBillingReportFilter } from '../components/customerBillingReport/CustomerBillingReportFilter'
import { ReportNavigationButtonContainer } from './ReportNavigationButtonContainer'
interface FlatServiceOrderExport {
  date: string
  serviceName: string
  containerNumber: string
  commodity: string
  quantity: number
  packaging: string
  grossWeight: number
  unit: string
}

export const GCServicesPrebillingContainer = observer(() => {
  const { reportsUIStore, customerBillingUIStore } = useBillingServiceStore()
  const { t } = useTranslate()

  const dataExportConfig = useMemo<IDataExportConfig<any>>(
    () => ({
      date: { header: t('date', 'Date'), type: 'string' },
      serviceName: { header: t('serviceName', 'Service name'), type: 'string' },
      containerNumber: { header: t('containerNumber', 'Container number'), type: 'string' },
      commodity: { header: t('commodity', 'Commodity'), type: 'string' },
      quantity: { header: t('quantity', 'Quantity'), type: 'number' },
      packaging: { header: t('packaging', 'Packaging'), type: 'string' },
      grossWeight: { header: t('grossWeight', 'Gross weight'), type: 'number' },
      unit: { header: t('unit', 'Unit'), type: 'string' },
    }),
    [t],
  )

  const dataExport = {
    title: t('generalCargoServices', 'General Cargo Services'),
    enableDataExport: true,
    dataExportConfig,
  }

  const columns = useMemo<MRT_ColumnDef<ServiceOrderReportDto>[]>(
    () => [
      {
        id: 'date',
        header: t('date', 'Date'),
        accessorKey: 'date',
        size: 120,
        ...tomTableColumnFilterPresets.dateOptional(),
      },
      {
        id: 'serviceName',
        header: t('serviceName', 'Service name'),
        accessorKey: 'serviceName',
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'containerNumber',
        header: t('containerNumber', 'Container number'),
        accessorKey: 'containerNumber',
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'commodity',
        header: t('commodity', 'Commodity'),
        accessorFn: row => row.packingLists[0]?.commodity,
        Cell: ({ cell }) => cell.getValue<string>() || EmptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'grossWeight',
        header: t('grossWeight', 'Gross weight'),
        accessorFn: row => row.packingLists[0]?.grossWeight,
        ...tomTableColumnFilterPresets.numericOptional(),
      },
      {
        id: 'unit',
        header: t('unit', 'Unit'),
        accessorFn: row => row.packingLists[0]?.unit,
        size: 80,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
    ],
    [t],
  )

  const defaultOptions = useDefaultMRTOptions<ServiceOrderReportDto>(undefined, dataExport)

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'billing/gc-services-pre-billing',
  )

  const table = useTomTable(
    {
      columns,
      data: customerBillingUIStore.serviceOrderData,
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      enableRowActions: false,
      initialState: {
        ...defaultOptions.initialState,
        columnPinning: {
          left: ['referenceNumber', 'containerNumber'],
        },
      },
      muiTableContainerProps: {
        sx: {
          minHeight: '450px',
        },
      },
    },
    undefined,
    dataExport,
    { columns, savedPreferences, onPreferencesChange },
  )

  const handleDownload = useMemo(
    () => () => {
      const rows = table.getPrePaginationRowModel().rows

      const dataToExport = rows.flatMap((row): FlatServiceOrderExport[] => {
        const item = row.original

        return item.packingLists.map(pl => ({
          date: item.date,
          serviceName: item.serviceName.toString(),
          containerNumber: item.containerNumber,
          commodity: pl.commodity || '',
          quantity: pl.quantity || 0,
          packaging: pl.packaging || '',
          grossWeight: pl.grossWeight || 0,
          unit: pl.unit?.toString() || '',
        }))
      })

      exportToCsv(
        t('serviceOrdersReport', 'Service Orders Report'),
        dataToExport,
        dataExportConfig,
        `Service_Orders_${dayjs().format('YYYYMMDD')}.csv`,
      )
    },
    [table, t, dataExportConfig],
  )

  useEffect(() => {
    if (reportsUIStore.currentStep === ReportStep.Report)
      customerBillingUIStore.fetchServiceOrderData()
  }, [reportsUIStore.currentStep, customerBillingUIStore])

  const finish = () => {
    customerBillingUIStore.resetData()
    reportsUIStore.resetStepper()
  }

  return (
    <>
      {reportsUIStore.currentStep === ReportStep.Filter && <CustomerBillingReportFilter />}
      {reportsUIStore.currentStep === ReportStep.Report && (
        <Box sx={{ width: '100%', mt: 1 }}>
          {customerBillingUIStore.isLoading ? (
            <Stack alignItems='center' py={5}>
              <CircularProgress />
            </Stack>
          ) : (
            <TomTable table={table} />
          )}
        </Box>
      )}
      <ReportNavigationButtonContainer
        disableNext={!customerBillingUIStore.customerId}
        downloadAction={() => handleDownload()}
        finishAction={finish}
      />
    </>
  )
})
